import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { AssignmentRendererProps } from './AssignmentRenderer.types'
import {
  DetailBooleanField,
  DetailCommentBlock,
  DetailField,
  DetailSection,
  UploadedFilesSection,
  RendererQueryState
} from '../common'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'
import { useGetAssignmentFloorPlanCertificationDetails } from 'dataQueries/assignmentDetails.query'
import { AssignmentAddonsSection } from './shared/AssignmentAddonsSection.component'

/**
 * Assignment detail renderer for Floor Plan Certification (Living Area Calculation).
 * Displays certification-specific fields (property type, floors, calculation method).
 */
export const LivingAreaCalcDetailsRenderer: FC<AssignmentRendererProps> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation(['gallery', 'purchase_flow'])
  const workspaceId = useAssignmentDetailDrawerWorkspaceId()
  const { data, isLoading, isError, refetch } = useGetAssignmentFloorPlanCertificationDetails(assignment.id, workspaceId)

  return (
    <RendererQueryState assignmentId={assignment.id} isLoading={isLoading} isError={isError} onRetry={refetch}>

      <DetailSection title={t('gallery:assignment_details.living_area_calc.certification.title')}>
        <DetailField
          label={t('gallery:assignment_details.living_area_calc.certification.property_type')}
          value={data?.propertyType
            ? t(`purchase_flow:floor_plan_certification_step.certification_property_types.${data.propertyType}`)
            : undefined
          }
        />
        <DetailField
          label={t('gallery:assignment_details.living_area_calc.certification.calculation_method')}
          value={data?.regulatedLivingAreaCalculation
            ? t(`purchase_flow:floor_plan_certification_step.area_calculations.${data.regulatedLivingAreaCalculation}`)
            : undefined
          }
        />
        <DetailBooleanField
          label={t('gallery:assignment_details.living_area_calc.certification.require_stamp')}
          value={data?.requireStampOfArchitect}
        />
      </DetailSection>

      {!!data?.floors?.length &&
        <DetailSection title={t('gallery:assignment_details.living_area_calc.floors.title')}>
          {data.floors.map((floor) => (
            <DetailField
              key={floor.floorNumber}
              label={`${t('gallery:assignment_details.living_area_calc.floors.floor')} ${floor.floorNumber}`}
              value={floor.name}
            />
          ))}
        </DetailSection>
      }

      {!!data?.comment &&
        <DetailSection title={t('gallery:assignment_details.living_area_calc.comments')}>
          <DetailCommentBlock>{data.comment}</DetailCommentBlock>
        </DetailSection>
      }

      <AssignmentAddonsSection assignment={assignment} order={order} mainProductId={mainProductId} />

      <UploadedFilesSection assignmentId={assignment.id} />

    </RendererQueryState>
  )
}
